import { useContext, useId, useState } from 'react';

import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon } from 'lucide-react';

import { Badge } from 'components/Badge';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { LTLShiplifyLookupStatus } from 'lib/api/getLTLQuickQuotes';
import { Undef } from 'types/UtilityTypes';
import { AccessorialIntegration, TMS } from 'types/enums/Integrations';
import { cn } from 'utils/shadcn';

import { LTLQuote, QuickQuoteInputs, RatingError } from '../types';
import { getLTLQuoteIdentity } from '../util';
import { LTLExternalQuote } from '../utils/classifyLTLQuoteResult';
import { LTLCompactRateList } from './LTLCompactRateList';
import { LTLExternalQuoteFeedback } from './LTLExternalQuoteFeedback';
import { LTLRateCard } from './LTLRateCard';
import { getTaiLimitedLiabilityCoverageConfig } from './Tai/limitedLiabilityCoverageConfig';

export enum LTLQuoteResultsVariant {
  // Rate cards for the single Quick Quote form.
  Full = 'full',
  // Dense rows that open into rate cards, for Batch LTL shipment cards.
  Compact = 'compact',
}

type LTLShiplifyLookups = NonNullable<
  NonNullable<QuickQuoteInputs['ltl']>['shiplifyLookups']
>;

function getShiplifySubmissionLabel(
  usedShiplify: boolean,
  usedShiplifyRecommendations: boolean
): string {
  if (!usedShiplify) {
    return 'Rate request submitted without Shiplify lookup';
  }

  if (usedShiplifyRecommendations) {
    return 'Rate request submitted with Shiplify recommendations';
  }

  return 'Rate request submitted after Shiplify verification';
}

type LTLQuoteResultsViewProps = {
  quotes: LTLQuote[];
  selectedQuoteIds: string[];
  externalQuote: LTLExternalQuote;
  shiplifyLookups: Undef<LTLShiplifyLookups>;
  onSelectQuote: (quote: LTLQuote) => void;
  isActiveTaiLtlIntegration?: boolean;
  variant?: LTLQuoteResultsVariant;
};

// LTL rates, rating errors, not-serviced carriers and the TMS quote link. Props only, so the
// single Quick Quote form and each Batch LTL shipment render the same results.
export function LTLQuoteResultsView({
  quotes,
  selectedQuoteIds,
  externalQuote,
  shiplifyLookups,
  onSelectQuote,
  isActiveTaiLtlIntegration = false,
  variant = LTLQuoteResultsVariant.Full,
}: LTLQuoteResultsViewProps) {
  const isCompact = variant === LTLQuoteResultsVariant.Compact;
  const instanceId = useId().replace(/:/g, '');
  const ratingErrorsPanelId = `ltl-rating-errors-panel-${instanceId}`;
  const notServicedPanelId = `ltl-not-serviced-panel-${instanceId}`;
  const { accessorialIntegrations, quickQuoteConfig } = useServiceFeatures();
  // Shiplify is Tai-only; match QuickQuoteForm submit gating.
  const shouldShowShiplifySubmissionBadge =
    isActiveTaiLtlIntegration &&
    accessorialIntegrations.some(
      (integration) => integration.name === AccessorialIntegration.Shiplify
    );
  const {
    currentState: { openExternalUrl },
  } = useContext(SidebarStateContext);
  const [showAllRates, setShowAllRates] = useState(false);

  const [showRatingErrors, setShowRatingErrors] = useState(false);
  const [showNotServiced, setShowNotServiced] = useState(false);

  const successfulQuotes = quotes
    .filter((q) => !q.isError && !q.notServiced)
    .sort((a, b) => a.totalCost - b.totalCost);

  const ratingErrors = quotes.filter((q) => q.isError && !q.notServiced);

  // Not serviced: Carrier can't service the lane (not fixable)
  const notServicedQuotes = quotes.filter((q) => q.notServiced);

  const groupedRatingErrors = ratingErrors.reduce<Record<string, LTLQuote[]>>(
    (acc, quote) => {
      const errorMessage = quote.errorMessage || 'Unable to quote';

      const matchedError = Object.values(RatingError).find((error) =>
        errorMessage.includes(error)
      );

      const message = matchedError || errorMessage;
      if (!acc[message]) {
        acc[message] = [];
      }
      acc[message].push(quote);
      return acc;
    },
    {}
  );

  const hideTaiLimitedLiability =
    externalQuote.source?.toLowerCase() === TMS.Tai &&
    quickQuoteConfig?.hideTaiLimitedLiability === true;
  const taiCoverageConfig = getTaiLimitedLiabilityCoverageConfig(
    externalQuote.source,
    hideTaiLimitedLiability
  );

  const visibleRates = showAllRates
    ? successfulQuotes
    : successfulQuotes.slice(0, 3);
  const hiddenCount = successfulQuotes.length - 3;
  const submittedShiplifyLookups = [
    shiplifyLookups?.pickup,
    shiplifyLookups?.dropoff,
  ].filter(Boolean);
  const usedShiplify = submittedShiplifyLookups.some(
    (lookup) =>
      lookup?.status === LTLShiplifyLookupStatus.Recommendations ||
      lookup?.status === LTLShiplifyLookupStatus.NoRecommendations
  );
  const usedShiplifyRecommendations = submittedShiplifyLookups.some(
    (lookup) => lookup?.status === LTLShiplifyLookupStatus.Recommendations
  );

  const shiplifySubmissionLabel = getShiplifySubmissionLabel(
    usedShiplify,
    usedShiplifyRecommendations
  );

  return (
    <Flex
      direction='col'
      gap='md'
      className='w-full mx-0'
      data-testid='ltl-quote-results'
    >
      {shouldShowShiplifySubmissionBadge && (
        <Badge
          variant='secondary'
          className={
            usedShiplify
              ? 'self-start bg-success-100 text-success-700'
              : 'self-start bg-neutral-100 text-neutral-700'
          }
        >
          {shiplifySubmissionLabel}
        </Badge>
      )}
      {isCompact ? (
        <LTLCompactRateList
          rateableQuotes={successfulQuotes}
          unavailableQuotes={[...ratingErrors, ...notServicedQuotes]}
          selectedQuoteIds={selectedQuoteIds}
          onSelectQuote={onSelectQuote}
          coverageConfig={taiCoverageConfig}
          hideCoverage={hideTaiLimitedLiability}
        />
      ) : (
        <>
          {/* Rates Section */}
          {successfulQuotes.length > 0 && (
            <>
              <Flex direction='col' gap='md' className='w-full mx-0'>
                {visibleRates.map((quote) => (
                  <LTLRateCard
                    key={getLTLQuoteIdentity(quote)}
                    quote={quote}
                    coverageConfig={taiCoverageConfig}
                    hideCoverage={hideTaiLimitedLiability}
                    isSelected={selectedQuoteIds.includes(
                      getLTLQuoteIdentity(quote)
                    )}
                    onClick={() => onSelectQuote(quote)}
                  />
                ))}
              </Flex>

              {hiddenCount > 0 && (
                <button
                  type='button'
                  onClick={() => setShowAllRates(!showAllRates)}
                  className='mt-3 mb-8 mx-auto py-2 px-6 text-sm text-neutral-800 rounded-lg border border-transparent hover:border-neutral-400 hover:shadow-sm hover:bg-neutral-100 transition-colors cursor-pointer flex items-center justify-center gap-2'
                >
                  {showAllRates ? 'Show Less' : `Show ${hiddenCount} More`}
                  {showAllRates ? (
                    <ChevronUpIcon className='w-4 h-4' />
                  ) : (
                    <ChevronDownIcon className='w-4 h-4' />
                  )}
                </button>
              )}
            </>
          )}

          {/* Rating Errors Section */}
          {ratingErrors.length > 0 && (
            <div className='w-full rounded-lg border border-error-400 overflow-hidden mt-2 mb-2'>
              <button
                type='button'
                onClick={() => setShowRatingErrors(!showRatingErrors)}
                aria-expanded={showRatingErrors}
                aria-controls={ratingErrorsPanelId}
                className={
                  'w-full px-4 py-2 bg-error-100/70 flex items-center justify-between transition-colors cursor-pointer'
                }
              >
                <Flex
                  align='center'
                  justify='between'
                  gap='sm'
                  className='w-full'
                >
                  <Typography
                    variant='body'
                    weight='medium'
                    className='text-error-600'
                  >
                    Rating Errors
                  </Typography>
                  <span className='inline-flex mx-2 px-2 py-0.5 bg-error-500/80 text-white rounded-full text-xs'>
                    {ratingErrors.length}
                  </span>
                </Flex>
                {showRatingErrors ? (
                  <ChevronDownIcon className='w-4 h-4 text-error-300' />
                ) : (
                  <ChevronRightIcon className='w-4 h-4 text-error-300' />
                )}
              </button>

              {showRatingErrors && (
                <div
                  id={ratingErrorsPanelId}
                  className='px-4 pt-2 pb-3 space-y-3 bg-error-100/70'
                >
                  {Object.entries(groupedRatingErrors).map(
                    ([message, carriers]) => (
                      <div key={message} className='pt-3 first:pt-0'>
                        <Flex className='items-start justify-between mb-1'>
                          <Typography
                            variant='body-sm'
                            weight='medium'
                            className='text-error-600'
                          >
                            {message}
                          </Typography>
                          <span className='text-xs bg-error-500/80 text-white px-2 py-0.5 ml-2 mt-1 rounded-full'>
                            {carriers.length}
                          </span>
                        </Flex>
                        <Flex direction='col' gap='xs'>
                          {carriers.map((c) => (
                            <Typography
                              key={c.id}
                              variant='body-xs'
                              className='text-[11px] text-neutral-800'
                            >
                              {`- ${c.carrierName}`}
                            </Typography>
                          ))}
                        </Flex>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          )}

          {/* Not Serviced Section */}
          {notServicedQuotes.length > 0 && (
            <div className='w-full rounded-lg border border-warning-400 bg-warning-50 overflow-hidden'>
              <button
                type='button'
                onClick={() => setShowNotServiced(!showNotServiced)}
                aria-expanded={showNotServiced}
                aria-controls={notServicedPanelId}
                className={cn(
                  'w-full px-4 py-2 flex items-center justify-between transition-colors cursor-pointer',
                  showNotServiced
                    ? 'bg-warning-100/80'
                    : 'hover:bg-warning-100/60'
                )}
              >
                <Flex
                  align='center'
                  justify='between'
                  gap='sm'
                  className='w-full text-warning-800'
                >
                  <Typography
                    variant='body'
                    weight='medium'
                    className='text-warning-800'
                  >
                    Not Serviced
                  </Typography>
                  <span className='inline-flex mx-2 px-2 py-0.5 bg-warning-500/90 text-white rounded-full text-xs'>
                    {notServicedQuotes.length}
                  </span>
                </Flex>
                {showNotServiced ? (
                  <ChevronDownIcon className='w-4 h-4 text-warning-500' />
                ) : (
                  <ChevronRightIcon className='w-4 h-4 text-warning-500' />
                )}
              </button>

              {showNotServiced && (
                <div id={notServicedPanelId} className='bg-warning-50/80'>
                  <table className='w-full text-xs'>
                    <thead>
                      <tr className='border-b border-warning-200'>
                        <th className='px-4 py-2 text-left font-medium text-warning-700'>
                          Carrier
                        </th>
                        <th className='px-4 py-2 text-left font-medium text-warning-700'>
                          Error Message
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {notServicedQuotes.map((q) => (
                        <tr
                          key={q.id}
                          className='border-b border-warning-200 last:border-b-0'
                        >
                          <td className='px-4 py-2 font-medium text-neutral-800'>
                            {q.carrierName}
                          </td>
                          <td className='px-4 py-2 text-neutral-600'>
                            {q.errorMessage || 'Cannot service this lane'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <LTLExternalQuoteFeedback
        externalQuote={externalQuote}
        onOpenQuote={openExternalUrl}
        isCompact={isCompact}
      />
    </Flex>
  );
}
